import { adminPublicPageSchema, type AdminPublicPage } from '@growup/accounts-shared';
import { Button, Card, Field, moduleOrigins, Notice, Textarea } from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';

import { getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';

/**
 * A pagina publica da pessoa (F-009, growup.gg/membros/<nickname>): se a
 * ativou e, para quem tem `members.manage`, esconde-la (moderacao) com um
 * motivo, ou voltar a mostra-la. A pessoa ve que foi escondida pela equipa.
 */
export function PublicPageCard({
  accountId,
  login,
  canManage,
}: {
  accountId: string;
  login: string;
  canManage: boolean;
}) {
  const base = `/api/members/members/${accountId}/public-page`;
  const [page, setPage] = useState<AdminPublicPage | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setPage(await getJson(base, adminPublicPageSchema));
    } catch (e) {
      setError(messageOf(e));
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  async function hide(hidden: boolean) {
    setBusy(true);
    setError(null);
    try {
      const body = hidden ? { hidden, reason } : { hidden };
      setPage(adminPublicPageSchema.parse(await send('PUT', `${base}/hidden`, body)));
      setReason('');
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  if (!page) return error ? <Notice tone="danger">{error}</Notice> : null;
  const url = `${moduleOrigins().site}/membros/${encodeURIComponent(login)}`;

  return (
    <Card headingLevel={2} title="Página pública">
      <div className="flex flex-col gap-3">
        <p>
          {page.enabled ? (
            <>
              Ativa{page.showPhoto ? ', com foto' : ', sem foto'}:{' '}
              <a href={url} target="_blank" rel="noreferrer" className="underline">
                {url}
              </a>
            </>
          ) : (
            'A pessoa não ativou a página pública.'
          )}
        </p>
        {page.hiddenAt ? (
          <Notice tone="warning">
            Escondida pela equipa em {formatDateTime(page.hiddenAt)}: {page.hiddenReason}
          </Notice>
        ) : null}
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {canManage && page.hiddenAt ? (
          <div>
            <Button variant="secondary" disabled={busy} onClick={() => void hide(false)}>
              Voltar a mostrar
            </Button>
          </div>
        ) : null}
        {canManage && !page.hiddenAt ? (
          <div className="flex flex-col gap-2">
            <Field label="Motivo para esconder (a pessoa não o vê)">
              <Textarea
                rows={2}
                maxLength={300}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
            <div>
              <Button
                variant="secondary"
                disabled={busy || reason.trim().length < 3}
                onClick={() => void hide(true)}
              >
                Esconder a página
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
